import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Toast = ({ show, type = 'success', title, message, onClose, duration = 4000 }) => {
  const { t } = useTranslation('common');
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  if (!show) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          borderColor: 'border-green-500',
          iconColor: 'text-green-500',
          bgColor: 'bg-white dark:bg-surface-dark',
          titleColor: 'text-text-main dark:text-white',
          messageColor: 'text-text-secondary dark:text-slate-400',
          icon: 'check_circle'
        };
      case 'warning':
        return {
          borderColor: 'border-amber-500',
          iconColor: 'text-amber-500',
          bgColor: 'bg-white dark:bg-surface-dark',
          titleColor: 'text-text-main dark:text-white',
          messageColor: 'text-text-secondary dark:text-slate-400',
          icon: 'warning'
        };
      case 'error':
        return {
          borderColor: 'border-red-500',
          iconColor: 'text-red-500',
          bgColor: 'bg-white dark:bg-surface-dark',
          titleColor: 'text-text-main dark:text-white',
          messageColor: 'text-text-secondary dark:text-slate-400',
          icon: 'error'
        };
      case 'info':
      case 'information':
        return {
          borderColor: 'border-blue-500',
          iconColor: 'text-blue-500',
          bgColor: 'bg-white dark:bg-surface-dark',
          titleColor: 'text-text-main dark:text-white',
          messageColor: 'text-text-secondary dark:text-slate-400',
          icon: 'info'
        };
      default:
        return {
          borderColor: 'border-green-500',
          iconColor: 'text-green-500',
          bgColor: 'bg-white dark:bg-surface-dark',
          titleColor: 'text-text-main dark:text-white',
          messageColor: 'text-text-secondary dark:text-slate-400',
          icon: 'check_circle'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 100, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 100, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-24 right-6 md:right-10 z-50"
        >
          <div className={`${styles.bgColor} border-l-4 ${styles.borderColor} shadow-lg rounded-r-lg p-4 flex items-start gap-3 max-w-sm`}>
            <div className={`${styles.iconColor} mt-0.5`}>
              <span className="material-symbols-outlined">{styles.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              {title && (
                <h4 className={`text-sm font-bold ${styles.titleColor}`}>{title}</h4>
              )}
              {message && (
                <p className={`text-xs ${styles.messageColor} ${title ? 'mt-1' : ''}`}>
                  {message}
                </p>
              )}
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className={`${styles.messageColor} hover:text-text-main dark:hover:text-white ml-2 flex-shrink-0 transition-colors`}
                aria-label={t('close')}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast;

